import { Image, Lottie, ThemeOptions } from '@melio/penny';
import { ComponentType, useEffect, useState } from 'react';

const animationCache = new Map<string, unknown>();

type IllustrationConfig = {
  src: string;
  type: 'animation' | 'image' | 'svg';
  /** For animations: when false, play once instead of looping. @default true */
  loop?: boolean;
};

const MELIO_ASSETS_BASE_URL = 'https://platform-static.meliopayments.com/assets/melio';

const melioIllustrationConfigs: Record<string, IllustrationConfig> = {
  gift: {
    src: `${MELIO_ASSETS_BASE_URL}/gift.lottie.json`,
    type: 'animation',
  },
  'add-card': {
    src: `${MELIO_ASSETS_BASE_URL}/add-card.lottie.json`,
    type: 'animation',
  },
  bank: {
    src: `${MELIO_ASSETS_BASE_URL}/bank.lottie.json`,
    type: 'animation',
  },
  'bank-success': {
    src: `${MELIO_ASSETS_BASE_URL}/bank-success.lottie.json`,
    type: 'animation',
  },
  'bank-missing': {
    src: `${MELIO_ASSETS_BASE_URL}/bank-missing.lottie.json`,
    type: 'animation',
  },
  blocked: {
    src: `${MELIO_ASSETS_BASE_URL}/blocked.lottie.json`,
    type: 'animation',
  },
  celebration: {
    src: `${MELIO_ASSETS_BASE_URL}/celebration.lottie.json`,
    type: 'animation',
  },
  'create-invoice': {
    src: `${MELIO_ASSETS_BASE_URL}/create-invoice.lottie.json`,
    type: 'animation',
  },
  'create-invoice-svg': {
    src: `${MELIO_ASSETS_BASE_URL}/create-invoice.svg`,
    type: 'svg',
  },
  'customer-add': {
    src: `${MELIO_ASSETS_BASE_URL}/customer-add.lottie.json`,
    type: 'animation',
  },
  declined: {
    src: `${MELIO_ASSETS_BASE_URL}/declined.lottie.json`,
    type: 'animation',
  },
  error: {
    src: `${MELIO_ASSETS_BASE_URL}/error.svg`,
    type: 'svg',
  },
  expired: {
    src: `${MELIO_ASSETS_BASE_URL}/expired.lottie.json`,
    type: 'animation',
  },
  mobile: {
    src: `${MELIO_ASSETS_BASE_URL}/mobile-v2.lottie.json`,
    type: 'animation',
  },
  'money-success': {
    src: `${MELIO_ASSETS_BASE_URL}/money-success.lottie.json`,
    type: 'animation',
  },
  'network-download': {
    src: `${MELIO_ASSETS_BASE_URL}/network-download.lottie.json`,
    type: 'animation',
  },
  'network-error': {
    src: `${MELIO_ASSETS_BASE_URL}/network-error.lottie.json`,
    type: 'animation',
  },
  'new-email': {
    src: `${MELIO_ASSETS_BASE_URL}/new-email.lottie.json`,
    type: 'animation',
    loop: false,
  },
  'no-items': {
    src: `${MELIO_ASSETS_BASE_URL}/no-items.lottie.json`,
    type: 'animation',
  },
  'payment-request-link': {
    src: `${MELIO_ASSETS_BASE_URL}/payment-request-link.lottie.json`,
    type: 'animation',
  },
  'pay-zero': {
    src: `${MELIO_ASSETS_BASE_URL}/pay-zero.lottie.json`,
    type: 'animation',
  },
  'page-not-found': {
    src: `${MELIO_ASSETS_BASE_URL}/page-not-found.lottie.json`,
    type: 'animation',
  },
  pending: {
    src: `${MELIO_ASSETS_BASE_URL}/pending.lottie.json`,
    type: 'animation',
  },
  notification: {
    src: `${MELIO_ASSETS_BASE_URL}/notification.lottie.json`,
    type: 'animation',
  },
  processing: {
    src: `${MELIO_ASSETS_BASE_URL}/processing.lottie.json`,
    type: 'animation',
  },
  security: {
    src: `${MELIO_ASSETS_BASE_URL}/security.svg`,
    type: 'svg',
  },
  sent: {
    src: `${MELIO_ASSETS_BASE_URL}/sent.lottie.json`,
    type: 'animation',
  },
  'small-business': {
    src: `${MELIO_ASSETS_BASE_URL}/small-business.lottie.json`,
    type: 'animation',
  },
  'small-business-svg': {
    src: `${MELIO_ASSETS_BASE_URL}/small-business.svg`,
    type: 'svg',
  },
  success: {
    src: `${MELIO_ASSETS_BASE_URL}/success.lottie.json`,
    type: 'animation',
  },
  'sync-accounts': {
    src: `${MELIO_ASSETS_BASE_URL}/sync-accounts.lottie.json`,
    type: 'animation',
  },
  'sync-user': {
    src: `${MELIO_ASSETS_BASE_URL}/sync-user.lottie.json`,
    type: 'animation',
  },
  'vendor-add': {
    src: `${MELIO_ASSETS_BASE_URL}/vendor-add.lottie.json`,
    type: 'animation',
  },
  'tax-form': {
    src: `${MELIO_ASSETS_BASE_URL}/tax-form.lottie.json`,
    type: 'animation',
  },
  together: {
    src: `${MELIO_ASSETS_BASE_URL}/together.lottie.json`,
    type: 'animation',
  },
  fast: {
    src: `${MELIO_ASSETS_BASE_URL}/fast.lottie.json`,
    type: 'animation',
  },
  'high-five': {
    src: `${MELIO_ASSETS_BASE_URL}/high-five.svg`,
    type: 'image',
  },
  payments: {
    src: `${MELIO_ASSETS_BASE_URL}/melio-payments.svg`,
    type: 'svg',
  },
  smb: {
    src: `${MELIO_ASSETS_BASE_URL}/melio-smb.svg`,
    type: 'svg',
  },
  hug: {
    src: `${MELIO_ASSETS_BASE_URL}/melio-hug.svg`,
    type: 'svg',
  },
  'accounting-platform-sync': {
    src: `${MELIO_ASSETS_BASE_URL}/accounting-platform-sync.json`,
    type: 'animation',
  },
  'onboarding-setting-up-for-you': {
    src: `${MELIO_ASSETS_BASE_URL}/onboarding-setting-up-for-you.lottie.json`,
    type: 'animation',
  },
};

const createLottieIllustration = (src: string, loop = true): ComponentType => {
  return function LottieIllustration() {
    const [animationData, setAnimationData] = useState<unknown>(animationCache.get(src) ?? null);

    useEffect(() => {
      if (animationData) {
        return;
      }

      fetch(src)
        .then((response) => response.json())
        .then((data) => {
          animationCache.set(src, data);
          setAnimationData(data);
        })
        .catch(() => {});
    }, [animationData]);

    if (!animationData) {
      return null;
    }

    return <Lottie animation={animationData} width="100%" height="100%" loop={loop} />;
  };
};

const createImageIllustration = (src: string, alt: string): ComponentType => {
  return function ImageIllustration() {
    return <Image src={src} alt={alt} height="auto" width="auto" />;
  };
};

const createIllustrationComponent = (key: string, config: IllustrationConfig): ComponentType => {
  if (config.type === 'animation') {
    return createLottieIllustration(config.src, config.loop ?? true);
  }
  return createImageIllustration(config.src, key);
};

const melioIllustrations = Object.fromEntries(
  Object.entries(melioIllustrationConfigs).map(([key, config]) => [key, createIllustrationComponent(key, config)])
) as ThemeOptions['illustrations'];

const MelioLightLogo = () => <Image src={`${MELIO_ASSETS_BASE_URL}/MelioLogo.svg`} alt="Melio" height="auto" width="auto" />;

const MelioDarkLogo = () => <Image src={`${MELIO_ASSETS_BASE_URL}/MelioLogoDark.svg`} alt="Melio" height="auto" width="auto" />;

export const melioTheme: ThemeOptions = {
  logos: {
    light: MelioLightLogo,
    dark: MelioDarkLogo,
  },
  borderRadii: {
    component: {
      container: {
        default: '24px',
      },
    },
  },
  illustrations: melioIllustrations,
};

export const prefetchIllustration = (illustrationType: string): void => {
  const config = melioIllustrationConfigs[illustrationType];
  if (!config || config.type !== 'animation') {
    return;
  }

  if (animationCache.has(config.src)) {
    return;
  }

  fetch(config.src)
    .then((response) => response.json())
    .then((data) => {
      animationCache.set(config.src, data);
    })
    .catch(() => {});
};
